import type { LocalOpportunityPayload } from "./types";
import { getOpportunityFieldLabel } from "./opportunityValidationMessages";

type FillItem = { field?: string; value?: unknown };

export const isEmptyFillTarget = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "number") return !Number.isFinite(value) || value <= 0;
  return value === undefined || value === null || String(value).trim() === "";
};

const toFormValue = (field: string, value: unknown): unknown => {
  if (field === "solutionOffered") {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((item) => String(item ?? "").trim())
      .filter(Boolean)
      .map((item) => (item.toLowerCase() === "other" ? "__other__" : item))
      .filter((item, index, array) => array.indexOf(item) === index);
  }
  return value;
};

type FormContact = { firstName: string; lastName: string; email: string; businessTitle: string; phone: string };

const contactsHaveData = (value: unknown): boolean =>
  Array.isArray(value) && value.some((contact) =>
    !!contact && typeof contact === "object" &&
    [
      (contact as any).firstName,
      (contact as any).lastName,
      (contact as any).email,
      (contact as any).businessTitle,
      (contact as any).phone,
    ]
      .some((field) => typeof field === "string" && field.trim() !== "")
  );

const normalizeContactsForForm = (value: unknown): FormContact[] => {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .filter((contact): contact is Record<string, unknown> => !!contact && typeof contact === "object")
    .map((contact) => ({
      firstName: String(contact.firstName ?? "").trim(),
      lastName: String(contact.lastName ?? "").trim(),
      email: String(contact.email ?? "").trim(),
      businessTitle: String((contact as any).businessTitle ?? "").trim(),
      phone: String(contact.phone ?? "").trim(),
    }))
    .filter((contact) =>
      contact.firstName || contact.lastName || contact.email || contact.businessTitle || contact.phone
    );
};

export const applyMandatoryFieldFillToForm = (
  form: LocalOpportunityPayload,
  defaultForm: LocalOpportunityPayload,
  filled: FillItem[] | Record<string, unknown> | undefined
): { form: LocalOpportunityPayload; applied: string[]; skipped: string[] } => {
  const applied: string[] = [];
  const skipped: string[] = [];
  const entries = Array.isArray(filled)
    ? filled.map((item) => [String(item?.field ?? "").trim(), item?.value] as const)
    : Object.entries(filled ?? {});
  if (entries.length === 0) return { form, applied, skipped };

  const formShape = defaultForm as Record<string, unknown>;
  const nextForm: Record<string, unknown> = { ...form };
  for (const [field, rawValue] of entries) {
    if (!field || !(field in formShape)) continue;

    if (field === "customerContacts") {
      const contacts = normalizeContactsForForm(rawValue);
      if (contacts.length === 0) continue;
      if (contactsHaveData(nextForm.customerContacts)) {
        skipped.push(field);
        continue;
      }
      nextForm.customerContacts = contacts;
      applied.push(field);
      continue;
    }

    let value = toFormValue(field, rawValue);
    if (value === undefined || value === null) continue;
    if (Array.isArray(formShape[field]) && !Array.isArray(value)) value = [value];
    if (field === "estimatedAwsMonthlyReccuringRevenue" && typeof value === "string") {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) continue;
      value = parsed;
    }
    if (isEmptyFillTarget(value)) continue;
    if (!isEmptyFillTarget(nextForm[field])) {
      skipped.push(field);
      continue;
    }
    nextForm[field] = value;
    applied.push(field);
  }

  return {
    form: applied.length ? nextForm as LocalOpportunityPayload : form,
    applied,
    skipped,
  };
};

export const buildMandatoryFieldFillSummaryMessages = (
  applied: string[],
  skipped: string[],
  fillData: {
    unresolved?: Array<{ field?: string; reason?: string }>;
    proposed?: Array<{ field?: string; value?: unknown; reason?: string }>;
    missingFields?: string[];
  } | undefined,
  validationErrorFields: string[] = []
): string[] => {
  const fieldLabel = (field: string): string => getOpportunityFieldLabel(field);
  const fieldList = (fields: string[]): string => fields.map(fieldLabel).join(", ");
  const aiFailure = getAiFailureMessage(fillData?.unresolved);
  if (aiFailure && applied.length === 0) return [aiFailure];

  if (
    Array.isArray(fillData?.missingFields) && fillData.missingFields.length === 0 &&
    applied.length === 0 && skipped.length === 0 &&
    !(fillData?.proposed ?? []).length && !(fillData?.unresolved ?? []).length &&
    validationErrorFields.length === 0
  ) {
    return ["All mandatory AWS fields are already filled, nothing to do."];
  }

  const messages: string[] = [];
  if (applied.length) {
    messages.push(`Filled ${applied.length} field${applied.length > 1 ? "s" : ""}: ${fieldList(applied)}.`);
    messages.push("Please review the filled values before saving.");
  } else {
    messages.push("No fields could be filled confidently from the available context.");
  }
  if (skipped.length) {
    messages.push(`Kept your existing values for: ${fieldList(skipped)}.`);
  }

  const proposed = (fillData?.proposed ?? []).filter(
    (item): item is { field: string; value: unknown; reason?: string } =>
      typeof item?.field === "string" &&
      item.field.trim() !== "" &&
      item?.value !== undefined &&
      item?.value !== null
  );
  for (const item of proposed.slice(0, 5)) {
    const value = typeof item.value === "string" ? item.value : JSON.stringify(item.value);
    messages.push(`Suggested ${fieldLabel(item.field)}: ${value}${item.reason ? `. Reason: ${item.reason}` : ""}`);
  }

  const unresolved = (fillData?.unresolved ?? [])
    .map((item) => item?.field)
    .filter((field): field is string => typeof field === "string" && field.trim() !== "");
  if (unresolved.length) {
    messages.push(`Still missing (not enough information): ${fieldList(unresolved)}.`);
    messages.push("Tip: add details to the AWS Mandatory Field Context field, then run the fill again.");
  }
  if (validationErrorFields.length) {
    messages.push(`Still needs attention before saving: ${fieldList(validationErrorFields)}.`);
  }
  return messages;
};

export const getAiFailureMessage = (
  unresolved: Array<{ reason?: string }> | undefined
): string | undefined => {
  const reason = (unresolved ?? [])
    .map((item) => item?.reason)
    .find((value) =>
      typeof value === "string" &&
      value.trim() !== "" &&
      /^AI fill failed:/i.test(value.trim())
    );
  if (!reason) return undefined;
  return reason.length > 500 ? `${reason.slice(0, 500)}...` : reason;
};

export const buildMandatoryFieldFillMessage = (data: any, awsOppId?: string): string => {
  const opportunityLabel = awsOppId?.trim()
    ? `AWS opportunity ${awsOppId.trim()}`
    : "this opportunity";
  const filledCount = Array.isArray(data?.filled) ? data.filled.length : 0;
  const unresolvedCount = Array.isArray(data?.unresolved) ? data.unresolved.length : 0;
  const proposedCount = Array.isArray(data?.proposed) ? data.proposed.length : 0;

  if (filledCount > 0) {
    return `Filled ${filledCount} AWS mandatory field${filledCount > 1 ? "s" : ""} for ${opportunityLabel}. Please review the values before syncing.`;
  }
  if (unresolvedCount > 0 || proposedCount > 0) {
    return `No fields were changed for ${opportunityLabel}. Some fields still need more context or manual review.`;
  }
  return `No missing AWS mandatory fields were found for ${opportunityLabel}.`;
};
